/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useVisitorSession.ts
 *
 * Rôle :
 *   Détecte si un visiteur a une session TNEVSID active (inscrit ou
 *   reconnecté par magic code). Sonde `/api/me/badge.php` : 200 ⇒
 *   connecté, 401 ⇒ anonyme. Sert à :
 *     - `HomePage` : `/` → /mon-espace (connecté) ou /role (anonyme)
 *       au lieu d'envoyer aveuglément tout le monde sur le dashboard.
 *     - `RequireVisitor` : protège les pages visiteur (renvoie vers
 *       /connexion si non connecté au lieu d'afficher un écran cassé).
 *
 *   Garde-fou UX : toute erreur réseau / offline ⇒ `anonymous` (un
 *   nouveau visiteur DOIT toujours pouvoir atteindre l'inscription ;
 *   un visiteur déjà connecté retombera juste sur l'écran d'entrée et
 *   pourra se reconnecter — dégradation acceptable, jamais bloquante).
 *
 * Réutilisable : 100 %. Pour une autre app DRANE, changer l'endpoint
 *   sonde si le modèle de session diffère.
 *
 * Voir : api/me/badge.php (visitor_require_auth, TNEVSID),
 *        _lib/visitor-magic-code.php, CLAUDE.md §10.16.
 *
 * @author Mika
 * @since  2026-05-17
 */

import { useQuery } from '@tanstack/react-query';
import { apiRequest, ApiError } from '@/api/client';

// FIX 2026-05-18 (bug Mika « /connexion tourne en rond ») — ajout d'un
// statut `error` DISTINCT de `anonymous`. AVANT : toute erreur 5xx/
// réseau de me/badge.php était dégradée en `anonymous` →
// RequireVisitor redirigeait vers /connexion → après code 2FA validé,
// navigate('/mon-espace') → me/badge.php re-500 → `anonymous` →
// /connexion → BOUCLE INFINIE (analogue exact du bug RoleGuard admin,
// cf. CLAUDE.md §24 2026-05-18). Se reconnecter ne corrige JAMAIS un
// 500 : on ne doit donc PAS traiter un 500 comme « non connecté ».
//   - 401  → anonymous (cas normal : redirection /connexion légitime).
//   - 5xx / réseau → error (RequireVisitor montre un écran « réessayer »,
//     PAS de redirection → boucle structurellement impossible).
export type VisitorSessionStatus =
  | 'loading'
  | 'authenticated'
  | 'anonymous'
  | 'error';

export interface UseVisitorSessionResult {
  readonly status: VisitorSessionStatus;
  readonly isAuthenticated: boolean;
  /**
   * FIX 2026-05-20 (compte plastron de démo) : rôle effectif du
   * visiteur connecté (lu depuis `me/badge.php → badge.role`). null
   * tant qu'on ne sait pas (loading / anonymous / error). Utilisé par
   * `usePlastronGuard()` pour décider d'intercepter les actions de
   * mutation côté front (defense in depth — le serveur refuse aussi
   * via `assert_not_plastron`).
   */
  readonly role: string | null;
  /** Force une nouvelle sonde (bouton « Réessayer » de RequireVisitor). */
  readonly refetch: () => void;
}

/** Clé de cache partagée — une seule sonde, réutilisée par toutes les pages. */
export const VISITOR_SESSION_QUERY_KEY = ['visitor-session'] as const;

/**
 * FIX 2026-05-20 (compte plastron de démo) : on stocke désormais l'état
 * { probe, role? } au lieu du seul probe — `role` permet à
 * usePlastronGuard() de détecter le rôle plastron côté client (UX
 * pédagogique au clic via la modale au lieu d'un toast après échec
 * serveur). Le serveur reste l'autorité (defense in depth).
 */
type ProbeKind = 'authed' | 'anon' | 'error';
interface ProbeData {
  readonly kind: ProbeKind;
  readonly role: string | null;
}

/**
 * Réponse minimale de `me/badge.php` consommée par cette sonde.
 * Le wrapper d'origine fait juste `apiRequest('me/badge.php')` —
 * désormais on type la réponse pour pouvoir lire `badge.role`.
 */
interface BadgeProbeResponse {
  readonly ok: true;
  readonly badge: {
    readonly role: string;
    readonly qr_payload?: string | null;
    readonly display_name?: string;
    readonly structure?: string | null;
  };
}

export function useVisitorSession(): UseVisitorSessionResult {
  const query = useQuery<ProbeData, Error>({
    queryKey: VISITOR_SESSION_QUERY_KEY,
    queryFn: async (): Promise<ProbeData> => {
      try {
        // FIX 2026-05-20 — on type la réponse pour extraire `badge.role`.
        const res = await apiRequest<BadgeProbeResponse>('/me/badge.php');
        const role = res.badge?.role ?? null;
        return { kind: 'authed', role };
      } catch (err) {
        // 401 = pas de session (cas normal, pas une panne) → anonyme.
        if (err instanceof ApiError && err.status === 401) {
          return { kind: 'anon', role: null };
        }
        // FIX 2026-05-18 — 5xx / réseau : NE PLUS dégrader en
        // « anonyme » (cause de la boucle infinie /connexion). On
        // renvoie `error` : RequireVisitor affichera un écran «
        // réessayer » au lieu de rediriger en boucle vers /connexion.
        return { kind: 'error', role: null };
      }
    },
    // Une session change rarement pendant une visite : 60 s de fraîcheur
    // évitent une sonde à chaque navigation. Pas de retry (un 401 n'est
    // pas transitoire).
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const refetch = () => {
    void query.refetch();
  };

  if (query.isPending) {
    return { status: 'loading', isAuthenticated: false, role: null, refetch };
  }
  const probe = query.data;
  const status: VisitorSessionStatus =
    probe?.kind === 'authed'
      ? 'authenticated'
      : probe?.kind === 'error'
        ? 'error'
        : 'anonymous';
  return {
    status,
    isAuthenticated: probe?.kind === 'authed',
    role: probe?.role ?? null,
    refetch,
  };
}
